import {
  CostCentersListDocument,
  CreateCostCenterDocument,
  EditCostCenterDocument,
  FinanceCostCentersDocument,
  FinanceCostCentersQuery,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'

export type CostCenter =
  FinanceCostCentersQuery['finance']['costCentersConnection']['nodes'][number]

interface UseCostCenterProps {
  ids?: number[]
  legalEntityIds: number[]
  skipRead?: boolean
}

export const useCostCenters = ({ ids, legalEntityIds, skipRead = false }: UseCostCenterProps) => {
  const { company } = useCurrentSession()

  const { loading, data, error } = useQuery(FinanceCostCentersDocument, {
    variables: { ids: ids, companyId: company.id, legalEntityIds: legalEntityIds },
    skip: !!skipRead,
    fetchPolicy: 'network-only',
  })

  const [editFinanceCostCenter] = useMutation(EditCostCenterDocument, {
    extractorFn: (dataParam) => dataParam.financeMutations.editFinanceCostCenter,
  })

  const renameCostCenter = useUnsafeCallback(
    async ({ id, name }: { id: number; name: string }) => {
      return await editFinanceCostCenter({
        variables: {
          id,
          companyId: company.id,
          name,
        },
        refetchQueries: [
          {
            query: FinanceCostCentersDocument,
            variables: { ids: [], legalEntityIds: [] },
          },
        ],
      })
    },
    [editFinanceCostCenter, company.id]
  )

  const editCostCenter = useUnsafeCallback(
    async ({
      id,
      name,
      code,
      description,
    }: {
      id: number
      name: string
      code: string
      description: string
    }) => {
      return await editFinanceCostCenter({
        variables: {
          id,
          companyId: company.id,
          name,
          code,
          description,
        },
        refetchQueries: [
          {
            query: FinanceCostCentersDocument,
            variables: { ids: [id], legalEntityIds: [] },
          },
          CostCentersListDocument,
        ],
      })
    },
    [editFinanceCostCenter, company.id]
  )

  return {
    loading,
    costCenters: data?.finance?.costCentersConnection.nodes,
    renameCostCenter,
    editCostCenter,
    error,
  }
}

export { FinanceCostCentersDocument, CreateCostCenterDocument }
